import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ExportPanel = ({ content }) => {
  const [selectedFormat, setSelectedFormat] = useState('pdf');
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [includeCharts, setIncludeCharts] = useState(true);

  const exportFormats = [
    {
      id: 'pdf',
      name: 'PDF Document',
      description: 'Professional format with layout preservation',
      icon: 'FileText',
      color: 'text-red-500',
    },
    {
      id: 'docx',
      name: 'Word Document',
      description: 'Editable format compatible with Microsoft Word',
      icon: 'FileText',
      color: 'text-blue-500',
    },
    {
      id: 'html',
      name: 'HTML Document',
      description: 'Web-ready format with styling',
      icon: 'Globe',
      color: 'text-green-500',
    },
    {
      id: 'markdown',
      name: 'Markdown',
      description: 'Plain text format with simple markup',
      icon: 'Hash',
      color: 'text-purple-500',
    },
  ];

  const templateOptions = [
    {
      id: 'standard',
      name: 'Standard Report',
      description: 'Clean, professional layout',
      preview: '/assets/templates/standard.png',
    },
    {
      id: 'executive',
      name: 'Executive Summary',
      description: 'Concise format for leadership',
      preview: '/assets/templates/executive.png',
    },
    {
      id: 'detailed',
      name: 'Detailed Analysis',
      description: 'Comprehensive format with sections',
      preview: '/assets/templates/detailed.png',
    },
  ];

  const handleExport = () => {
    console.log('Exporting content:', {
      format: selectedFormat,
      includeMetadata,
      includeCharts,
      content,
    });
    alert(`Exporting as ${selectedFormat?.toUpperCase()}...`);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Export Format</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {exportFormats?.map((format) => (
            <div
              key={format?.id}
              onClick={() => setSelectedFormat(format?.id)}
              className={`bg-card border-2 rounded-lg p-6 cursor-pointer transition-all hover:shadow-md ${
                selectedFormat === format?.id ? 'border-primary' : 'border-border'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg bg-muted/50 flex items-center justify-center ${format?.color}`}>
                  <Icon name={format?.icon} size={24} />
                </div>
                <div
                  className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                    selectedFormat === format?.id
                      ? 'bg-primary border-primary' :'border-border'
                  }`}
                >
                  {selectedFormat === format?.id && <Icon name="Check" size={14} color="white" />}
                </div>
              </div>
              <h4 className="font-semibold text-foreground mb-2">{format?.name}</h4>
              <p className="text-sm text-muted-foreground">{format?.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold text-foreground mb-4">Report Template</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {templateOptions?.map((template) => (
            <div
              key={template?.id}
              className="bg-card border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
            >
              <div className="h-40 bg-muted flex items-center justify-center">
                <Icon name="FileText" size={48} color="var(--color-muted-foreground)" />
              </div>
              <div className="p-4">
                <h4 className="font-semibold text-foreground mb-1">{template?.name}</h4>
                <p className="text-sm text-muted-foreground mb-3">{template?.description}</p>
                <button className="text-sm text-primary hover:text-primary/80">
                  Select Template
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Export Options</h3>
        <div className="space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={includeMetadata}
              onChange={(e) => setIncludeMetadata(e?.target?.checked)}
              className="mt-1 w-5 h-5 text-primary rounded border-border focus:ring-primary"
            />
            <div>
              <p className="font-medium text-foreground">Include Metadata</p>
              <p className="text-sm text-muted-foreground">
                Add generation timestamp, data sources, and AI confidence scores
              </p>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={includeCharts}
              onChange={(e) => setIncludeCharts(e?.target?.checked)}
              className="mt-1 w-5 h-5 text-primary rounded border-border focus:ring-primary"
            />
            <div>
              <p className="font-medium text-foreground">Include Charts and Visualizations</p>
              <p className="text-sm text-muted-foreground">
                Embed referenced charts, graphs, and data visualizations
              </p>
            </div>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              defaultChecked
              className="mt-1 w-5 h-5 text-primary rounded border-border focus:ring-primary"
            />
            <div>
              <p className="font-medium text-foreground">Apply Company Branding</p>
              <p className="text-sm text-muted-foreground">
                Include company logo, colors, and footer information
              </p>
            </div>
          </label>
        </div>
      </div>

      <div className="flex items-center justify-between pt-6 border-t border-border">
        <div className="text-sm text-muted-foreground">
          {content ? `Content ready for export (${content?.length} characters)` : 'No content to export'}
        </div>
        <div className="flex items-center gap-3">
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-muted text-foreground rounded-lg hover:bg-muted/80 transition-colors">
            <Icon name="Eye" size={18} />
            Preview
          </button>
          <button
            onClick={handleExport}
            disabled={!content}
            className="inline-flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Icon name="Download" size={18} />
            Export Report
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportPanel;